/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Tomáš Burian (burian@cadwork.cz)
 *
 */

import {SurfaceColor, EEmbededElementTypes, RenderDocument, StorageUtils} from "js_renderer";
import Element from "./element";
import {vec2, vec3} from "gl-matrix";
import IBoundingBox from "../bounding_box_interface";
import Factory from "./factory";

/**
 * Ground plane scene api element
 * @param {cadwork.webgl.SceneAPI.Scene} aSceneAPI
 * @extends cadwork.webgl.SceneAPI.elements.Element
 * @constructor
 */
export default
class GroundPlane extends Element {

    public static TypeName: string = EEmbededElementTypes.GROUNDPLANE;

    private _min: number[];
    private _max: number[];
    private _center: vec3;
    private _scale: vec2;
    private _color: vec3;
    private _positions: number[];
    private _hasGrass: boolean;
    private _urlTexture: string;
    private _urlNormal: string;
    private _useTexture: boolean;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._min = [];
        this._max = [];
        this._center = vec3.create();
        this._scale = vec2.fromValues(this._sceneAPI.groundPlaneModule.getGroundPlaneScale(), this._sceneAPI.groundPlaneModule.getGroundPlaneScale());
        this._color = this.setColor(this._sceneAPI.groundPlaneModule.getGroundPlaneColor());
        this._positions = [];
        this._hasGrass = false;

        this._urlTexture = '@assets/images/environment/grass.png';
        this._urlNormal = '@assets/images/environment/grass-nm.png';
        this._useTexture = false;
    }

    public static create(aRenderDocument: RenderDocument): GroundPlane {
        let element = new GroundPlane(aRenderDocument);
        element._typeName = GroundPlane.TypeName;
        return element;
    }

    /**
     * Calculate new positions based on the bounding box of the model
     *
     * @param {boolean} aUp
     * @param {cadwork.webgl.SceneAPI.BoundingBox} aBBox
     * @param {number} aScale
     */
    public set(aUp: boolean, aBBox: IBoundingBox, aScale: number) {
        this._center = aBBox.getCenter();
        this._scale[0] = aScale;
        this._scale[1] = aScale;

        let wx = (aBBox.getMax()[0] - aBBox.getMin()[0]) * 0.5;
        let wz = (aBBox.getMax()[2] - aBBox.getMin()[2]) * 0.5;

        wx *= this._scale[0];
        wz *= this._scale[1];

        this._min = [this._center[0] - wx, aBBox.getMin()[1], this._center[2] - wz];
        this._max = [this._center[0] + wx, aBBox.getMin()[1], this._center[2] + wz];

        if (aUp) {
            this._hasGrass = true;
        } else {
            this._max[1] -= 0.001;
        }

        this._positions = [
            this._min[0], this._max[1], this._max[2],
            this._max[0], this._max[1], this._max[2],
            this._max[0], this._max[1], this._min[2],
            this._min[0], this._max[1], this._min[2]
        ];
    }

    /**
     * Set ground plane color
     * @param {vec3} aColor Diffuse color. Vector of 3 values in range <0, 255>
     */
    public setColor(aColor: vec3) {
        // renderer expects values in <0, 1> range
        let colorZeroToOne = vec3.fromValues(aColor[0] / 255.0, aColor[1] / 255.0, aColor[2] / 255.0);
        this._color = vec3.clone(colorZeroToOne);

        // if the model is already rendered then we update the color in the renderer
        if (this._renderDocumentProperties.renderModel) {
            // get/create surfaceColor from/in material library
            // example of adding a new material to the material library
            /*let materialLibrary = this._renderDocumentProperties.renderModel.document.materialLibrary;
            let surfaceColor = cadwork.webgl.geometry.material.SurfaceColor.getDefaultValues();
            surfaceColor.diffuseColor = vec3.clone(aColor);
            let matchedObject = materialLibrary.matchObject(surfaceColor, "surfaceColors", null);
            if (!matchedObject) {
                matchedObject = materialLibrary.createObjectFromJSON(null, surfaceColor, cadwork.webgl.geometry.material.SurfaceColor.TypeName, null);
                materialLibrary.addObject(
                    matchedObject,
                    "surfaceColors"
                );
            }*/

            // we can use this approach because the renderable have reference to the surface color item rom the library
            let geometryObjects = this._renderDocumentProperties.renderModel.getGeometryObjects();
            for (let i = 0; i < geometryObjects.length; i++) {
                let object = geometryObjects[i];
                object.appearance[SurfaceColor.TypeName].diffuseColor = colorZeroToOne;
            }
        }
    }

    /**
     * @param {boolean} value
     */
    public useTexture(value: boolean) {
        this._useTexture = value;
    }

    protected _generateGeometryObjectDescriptionJSON(): any {
        let appearanceContent: any = {};

        if (this._useTexture) {
            appearanceContent.imageTexture = {
                url: [this._urlTexture],
                width: [0],
                height: [0],
                offsets: [0],
                sizes: [0],
                tiled: [false],
                alpha: false,
                model: "REPLACE",
                blendColor: vec3.fromValues(0.0, 0.5, 0.0),
                repeatS: true,
                repeatT: true
            };
        }

        appearanceContent.material = {
            diffuseColor: vec3.clone(this._color),
            ambientColor: vec3.fromValues(0.2, 0.2, 0.2)
        };

        appearanceContent.renderOption = {
            vertexOrdering: "CCW"
        };

        return [{
            description: {
                type: "SHAPE",
                bboxMin: this._min,
                bboxMax: this._max,
                contributeToNearPlaneClipping: false,
                hasGrass: this._hasGrass
            },
            appearance: appearanceContent,
            indexedFaceSet: {
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceUri(),
                vertexCount: 4,
                normalCount: 4,
                texcoordCount: this._useTexture ? 4 : 0,
                indexCount: 6,
                binSize: 0,
                positions: this._positions,
                normals: this._hasGrass ?
                    [0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0] :
                    [0.0, -1.0, 0.0, 0.0, -1.0, 0.0, 0.0, -1.0, 0.0, 0.0, -1.0, 0.0],
                texCoords: this._useTexture ?
                    [0.0, 0.0, 0.0, 2.0 * this._scale[1], 2.0 * this._scale[0], 2.0 * this._scale[1], 2.0 * this._scale[1], 0.0] :
                    [],
                indices: this._hasGrass ?
                    [0, 1, 2, 0, 2, 3] :
                    [2, 1, 0, 3, 2, 0]
            }
        }];
    }

    protected _generateElementDescriptionJSON(): any {
        return {
            type: this.getTypeName(),
            id: this.getTypeName() + "_" + this._UID,   // this is is not important in the engine, it can identify e.g. id of an element in Lexocad
            UID: this._UID
        };
    }
}

Factory.register(GroundPlane);
